import { useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import settings from '../../../settings'

import { tr } from 'mmfintech-commons'
import { useEventTracker } from 'mmfintech-checkout-commons'

import { ErrorDisplay } from 'mmfintech-portal-commons'
import { CustomCheckbox, MethodButton } from '@components'
import { MethodSelectionContainer, VoucherTitle } from '../Voucher/CheckoutVoucher.styled'

import type { VoucherOption } from '../../../@types'

import RedeemIcon from '@assets/icons/redeem.png'
import PurchaseIcon from '@assets/icons/purchase.png'

type BlixcardOptionsStepProps = {
  sessionId: string
  option: VoucherOption
  setOption: (option: VoucherOption) => void
  handleNext: () => void
}

export const BlixcardOptionsStep = ({ sessionId, option, setOption, handleNext }: BlixcardOptionsStepProps) => {
  const { checkoutPayError, checkoutPayFetching, sessionError } = useSelector(
    ({ checkout: { checkoutPayError, checkoutPayFetching, sessionError } }) => ({
      checkoutPayError,
      checkoutPayFetching,
      sessionError
    }),
    shallowEqual
  )

  const [agree, setAgree] = useState<boolean>(true)

  const { trackEvent } = useEventTracker()

  const toggleAgree = () => {
    void trackEvent(sessionId, 'terms_checkbox_clicked')
    setAgree(prevState => !prevState)
  }

  const prepareLink = (caption: string) => {
    const list = caption.split('<a>')
    const prefix = list.length > 0 ? list[0] : ''
    const remain = list.length > 1 ? list[1] : ''
    const list2 = remain.split('</a>')
    const inner = list2.length > 0 ? list2[0] : ''
    const suffix = list2.length > 1 ? list2[1] : ''
    return (
      <>
        {prefix}
        <a
          href={settings.termsUrl}
          target='_blank'
          rel='noreferrer'
          onClick={() => trackEvent(sessionId, 'terms_link_clicked')}
        >
          {inner}
        </a>
        {suffix}
      </>
    )
  }

  const handleButtonClick = (value: VoucherOption) => {
    if (agree && !checkoutPayFetching) {
      if (option === value) {
        handleNext()
      } else {
        void trackEvent(sessionId, `${value}_voucher_clicked`)
        setOption(value)
      }
    }
  }

  return (
    <>
      <VoucherTitle data-test='select-option-page'>
        {tr('CHECKOUT.VOUCHER.OPTIONS.TITLE', 'Select method')}
      </VoucherTitle>

      <ErrorDisplay error={[sessionError, checkoutPayError]} />

      <MethodSelectionContainer>
        <MethodButton
          icon={PurchaseIcon}
          caption={tr('CHECKOUT.BLIXCARD.OPTIONS.BUY', 'Buy')}
          description={tr(
            'CHECKOUT.BLIXCARD.OPTIONS.BUY_DESCRIPTION',
            'You can purchase Blixcard via bank transfer instantly'
          )}
          selected={option === 'buy'}
          disabled={!agree || Boolean(sessionError) || Boolean(checkoutPayError)}
          onClick={() => handleButtonClick('buy')}
          data-test='buy-option'
        />
        <MethodButton
          icon={RedeemIcon}
          caption={tr('CHECKOUT.BLIXCARD.OPTIONS.REDEEM', 'Redeem')}
          description={tr(
            'CHECKOUT.BLIXCARD.OPTIONS.REDEEM_DESCRIPTION',
            'If you already have Blixcard you can redeem from here'
          )}
          selected={option === 'redeem'}
          disabled={!agree || Boolean(sessionError) || Boolean(checkoutPayError)}
          onClick={() => handleButtonClick('redeem')}
          data-test='redeem-option'
        />
      </MethodSelectionContainer>

      <CustomCheckbox
        data-test='agree_checkbox'
        checked={agree}
        toggle={toggleAgree}
        text={
          settings.termsUrl
            ? prepareLink(tr('CHECKOUT.VOUCHER.OPTIONS.AGREE_LINK', 'I agree to the <a>Terms and Conditions</a>'))
            : tr('CHECKOUT.VOUCHER.OPTIONS.AGREE', 'I agree to the Terms and Conditions')
        }
      />
    </>
  )
}
